import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as ShieldIcon } from '@elementary/common-ui/assets/20x20/shield.svg';
import { ReactComponent as WrenchIcon } from '@elementary/common-ui/assets/20x20/wrench.svg';
import { ReactComponent as BetaTag } from '@elementary/common-ui/assets/beta-tag-grey.svg';
import {
  Breadcrumbs,
  Flex,
  FlexColumn,
  FlexSpace,
  GenerateAIButton,
  Tab,
  Tabs,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';

export type CatalogTab = 'metadata' | 'coverage';

const TabsStyled = styled(Tabs)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray8};
`;

type TabData = {
  label: string;
  value: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isBeta?: boolean;
};

type Props = {
  selectedTab: CatalogTab;
  onChange: (val: CatalogTab) => void;
};

const CatalogHeader = ({ selectedTab, onChange }: Props) => {
  const { data: account } = useAccount();
  const hasPermission = useEnvPermission();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Catalog',
  });

  const tabs = useMemo(() => {
    const tabs: TabData[] = [
      { label: 'Metadata', value: 'metadata', icon: WrenchIcon },
    ];
    if (hasPermission('VIEW_TESTS')) {
      tabs.push({
        label: 'Test coverage',
        value: 'coverage',
        icon: ShieldIcon,
        isBeta: true,
      });
    }
    return tabs;
  }, [hasPermission]);

  return (
    <FlexColumn sx={{ gap: 2 }}>
      <FlexSpace sx={{ alignItems: 'center' }}>
        <Breadcrumbs items={breadcrumbs} />
        {account?.ai_features_enabled && (
          <GenerateAIButton>Explore assets with AI</GenerateAIButton>
        )}
      </FlexSpace>
      <TabsStyled
        value={selectedTab}
        onChange={(_, val) => onChange(val)}
        orientation="horizontal"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.label}
            label={
              <Flex sx={{ gap: 1 }}>
                {tab.label}
                {tab.isBeta && <BetaTag />}
              </Flex>
            }
            value={tab.value}
            $size="small"
            iconPosition="start"
            icon={<tab.icon width={20} />}
          />
        ))}
      </TabsStyled>
    </FlexColumn>
  );
};

export default CatalogHeader;
